import { Component, h, Prop, State, Watch } from '@stencil/core';
import { fetchDaysAvailability, fetchPossibleHours } from '../../api';
import { BookingDetails, CartItem, ConnectedArticle, ItemDetails, PriceOptions, TimeSlotPicker, Widget } from '../../redux/types';
import { Unsubscribe } from '@stencil/redux';
import { store } from '../../redux/store';
import { Article, DateChanged, DetailsComponents, DetailsMode, IncludedArticle, Picker, ValidateAndFetchPriceProps } from './utils/types';
import { t } from '../../redux/utils';
import {
  addItemToCart,
  canRemoveItemFromCart,
  changeBookingDate,
  changeBookingLocation,
  changeBookingMileage,
  changeBookingVariation,
  changeMileageOption,
  fetchItemsForCurrentStep,
  fetchPrice,
  initForOrder,
  openCheckout,
  removeItemFromCart,
  requireInit,
} from '../../redux/actions';
import { itemDescriptionKey, itemKey, itemTitleKey } from '../../utils/trans';
import { CartStatus, DaysAvailability, DayState, EnabledDisabled, Price, PriceCalculation } from '../../types/types';
import { dateToString, priceWithMileagePerTimeT, twoMonthDateRange } from '../../utils/utils';
import { mileageIdInBookingDetails, thisItemInCart, variationIdInBookingDetails } from './utils/utils';
import {
  available,
  bookingDateChanged,
  bookingMileageChanged,
  bookingVariationChanged,
  calculatedToDate,
  changeReason,
  dateNoTime,
  dateTime,
  fetchDropOffHours,
  fetchPickupHours,
  noDateNoTime,
  noDateTime,
  validateAndFetchPrice,
} from './utils/picker';
import { isSameDay, isSameMonth } from 'date-fns';
import { RentwareCheckoutTemplate } from '../rentware-checkout/rentware-checkout-modal/template/rentware-checkout-template';

@Component({
  tag: 'rtr-article-details-state',
  shadow: false,
})
export class RtrArticleDetailsState {
  @Prop() itemId: string;
  @Prop() mode: DetailsMode = DetailsMode.Search;
  @Prop() bookingDetails: BookingDetails;
  @Prop() currency: string;
  @Prop() currencyPosition: string;
  @Prop() widget: Widget;
  @Prop() locale: string;
  @Prop() timePicker: TimeSlotPicker;
  @Prop() priceOptions: PriceOptions;
  @Prop() itemDetails: ItemDetails;
  @Prop() onCloseButtonClick: () => void;
  @Prop() cartStatus: CartStatus;
  @Prop() loadedComponents: { [name: string]: boolean };
  @Prop() scrollToCalendar: boolean;
  @Prop() showLocation: EnabledDisabled;

  @State() showFullGallery: boolean = false;
  @State() isGalleryContainerHidden: boolean = false;
  @State() isInfoContainerVisible: boolean = false;
  @State() inCartOpened: boolean = false;

  @State() error: boolean = false;
  @State() possibleHours: {} = {};
  @State() suspectedDropOffDate: Date = null;
  @State() dropOffDateAsPickupDate: boolean = false;
  @State() price: Price = null;
  @State() amountToBook: number = 1;
  @State() daysAvailability: DaysAvailability;
  @State() firstAvailableDate = null;
  @State() selectedMonth: Date = new Date();

  @State() isWaitingForApiUpdate: boolean = false;
  @State() isCalendarLoading: boolean = false;
  @State() loadingPickUpHours: boolean = false;
  @State() loadingDropOffHours: boolean = false;

  unsubscribe!: Unsubscribe;
  initForOrder!: typeof initForOrder;
  addItemToCart!: typeof addItemToCart;
  removeItemFromCart!: typeof removeItemFromCart;
  fetchItemsForStep!: typeof fetchItemsForCurrentStep;
  canRemoveItemFromCart!: typeof canRemoveItemFromCart;
  changeMileageOption!: typeof changeMileageOption;
  fetchPrice!: typeof fetchPrice;
  requireInit!: typeof requireInit;
  changeBookingDate!: typeof changeBookingDate;
  changeBookingMileage!: typeof changeBookingMileage;
  changeBookingVariation!: typeof changeBookingVariation;
  changeBookingLocation!: typeof changeBookingLocation;
  openCheckout!: typeof openCheckout;
  errorTimer: ReturnType<typeof setTimeout>;

  componentWillLoad() {
    store.mapDispatchToProps(this, {
      fetchItemsForStep: fetchItemsForCurrentStep,
      addItemToCart,
      removeItemFromCart,
      initForOrder,
      canRemoveItemFromCart,
      changeMileageOption,
      fetchPrice,
      requireInit,
      changeBookingDate,
      changeBookingMileage,
      changeBookingVariation,
      changeBookingLocation,
      openCheckout,
    });

    if (!this.bookingDetails.selectedLocationId && this.itemDetails?.location?.id) {
      this.changeBookingLocation(this.itemDetails.location.id);
    }

    this.selectedMonth = this.bookingDetails.toDate || this.bookingDetails.fromDate || new Date();
    this.initCalendar();
  }

  componentWillRender() {
    if (this.amountToBook < this.itemDetails?.minQuantity) {
      this.amountToBook = this.itemDetails.minQuantity;
    }
  }

  disconnectedCallback() {
    clearInterval(this.errorTimer);
  }

  @Watch('bookingDetails')
  watchBookingDetailsHandler(newDetails: BookingDetails, oldDetails: BookingDetails) {
    //call if cart becomes empty
    if (newDetails.orderDetails?.id !== oldDetails.orderDetails?.id) {
      this.fetchCalendar(newDetails);
      validateAndFetchPrice(this.makeValidateAndFetchPriceProps());
    } else if (bookingDateChanged(newDetails, oldDetails)) {
      this.fetchCalendar(newDetails, changeReason(newDetails, oldDetails));
    } else if (bookingVariationChanged(newDetails, oldDetails, this.itemDetails.id) || bookingMileageChanged(newDetails, oldDetails, this.itemDetails.id)) {
      this.fetchCalendar(newDetails);
    }
  }

  @Watch('daysAvailability')
  watchDaysAvailabilityHandler() {
    // this.fetchCalendar(this.bookingDetails, { bookingTime: true });
    validateAndFetchPrice(this.makeValidateAndFetchPriceProps());
  }

  @Watch('suspectedDropOffDate')
  watchSuspectedDropOffDateHandler() {
    validateAndFetchPrice(this.makeValidateAndFetchPriceProps());
  }

  get selectedMileageId() {
    return mileageIdInBookingDetails(this.itemId, this.bookingDetails);
  }

  get selectedVariationId() {
    // variationId in details is not yet updated
    if (!variationIdInBookingDetails(this.itemId, this.bookingDetails) && this.itemDetails.variation) {
      return this.itemDetails.variation.items[0].id;
    }
    return variationIdInBookingDetails(this.itemId, this.bookingDetails);
  }

  initCalendar = () => {
    if (this.itemDetails.mileageOptions && !this.selectedMileageId) {
      this.changeBookingMileage({ [this.itemId]: this.itemDetails.mileageOptions[0].id });
    }

    if (this.itemDetails.variation && !this.selectedVariationId) {
      this.changeBookingVariation({ [this.itemId]: this.itemDetails.variation.items[0].id });
    }
    this.fetchCalendar(this.bookingDetails);
  };

  get makeTimeSlot(): TimeSlotPicker {
    if (!this.timePicker) {
      return null;
    }

    return {
      ...this.timePicker,
      selectedOption: this.bookingDetails.selectedTimeSlot,
    };
  }

  fetchCalendar = (bookingDetails: BookingDetails, changeReason?: DateChanged) => {
    // reason is used to avoid not necessary calls,
    // if no reason all calls are made for given state
    if (
      (!this.withDateRange || !bookingDetails.fromDate) && //
      (!changeReason || changeReason.reset)
    ) {
      this.fetchPickUpDays();
    } else {
      if (
        this.withDateRange && //
        (!changeReason || changeReason.bookingDate)
      ) {
        this.fetchDropOffDays();
      }

      if (
        this.withTime && //
        (!changeReason || changeReason.bookingDate)
      ) {
        fetchPickupHours({
          itemId: this.itemId,
          selectedVariationId: this.selectedVariationId,
          fromDate: bookingDetails.fromDate,
          toDate: bookingDetails.toDate || this.suspectedDropOffDate,
          suspectedDropOffDate: this.suspectedDropOffDate,
          withDateRange: this.withDateRange,
          changeFromTimeCallback: this.handlePickUpTimeChange,
          changeToTimeCallback: this.handleDropOffTimeChange,
          changeBookingDateCallback: this.changeBookingDate,
          setPossibleHoursCallback: this.setPossibleHours,
          loadingCallback: this.setLoadingPickup,
          fetchOnly: true,
        });
      }
    }
    const askForDropOffHours: boolean =
      bookingDetails.fromTime &&
      this.withTime && //
      (calculatedToDate(bookingDetails, this.daysAvailability) || bookingDetails.toDate) &&
      (!changeReason || changeReason.bookingTime || changeReason.toDate);

    if (askForDropOffHours) {
      fetchDropOffHours({
        itemId: this.itemId,
        selectedVariationId: this.selectedVariationId,
        fromDate: bookingDetails.fromDate,
        toDate: bookingDetails.toDate,
        fromTime: bookingDetails.fromTime,
        suspectedDropOffDate: this.suspectedDropOffDate,
        changeBookingDateCallback: this.changeBookingDate,
        changeToTimeCallback: this.handleDropOffTimeChange,
        setPossibleHoursCallback: this.setPossibleHours,
        loadingCallback: this.setLoadingDropOff,
        fetchOnly: true,
      });
    }

    if ((this.withTime && (changeReason?.toTime || changeReason?.timeSlot)) || (!this.withTime && changeReason?.toDate)) {
      validateAndFetchPrice(this.makeValidateAndFetchPriceProps());
    }
  };

  makeValidateAndFetchPriceProps = (customProps: Partial<ValidateAndFetchPriceProps> = {}) => {
    return {
      itemId: this.itemId,
      selectedVariationId: this.selectedVariationId,
      fromDate: this.bookingDetails.fromDate,
      toDate: this.bookingDetails.toDate,
      suspectedDropOffDate: this.suspectedDropOffDate,
      fromTime: this.bookingDetails.fromTime,
      toTime: this.bookingDetails.toTime,
      withDateRange: this.withDateRange,
      withTime: this.withTime,
      amountToBook: this.amountToBook,
      selectedTimeSlot: this.bookingDetails.selectedTimeSlot,
      priceOptions: this.priceOptions,
      loadingCallback: this.setLoading,
      setPriceCallback: this.setPrice,
      setPriceOptionsCallback: this.setPriceOptions,
      fetchPriceCallback: this.fetchPrice,
      runWithLoading: true,
      ...customProps,
    };
  };

  handleTimePickerOptionChange = (newTimeSlot: string) => {
    this.changeBookingDate({ selectedTimeSlot: newTimeSlot });
  };

  handleGalleryContainerVisibility = (show: boolean) => {
    this.isGalleryContainerHidden = show;
  };

  handleInfoContainerVisibility = (show: boolean) => {
    this.isInfoContainerVisible = show;
  };

  handleCloseButtonClick = () => {
    if (this.mode === DetailsMode.Gallery) {
      this.onCloseButtonClick();
    }

    if (this.showFullGallery) {
      this.showFullGallery = false;
      return;
    }

    if (this.inCartOpened) {
      this.inCartOpened = false;
      return;
    }

    this.onCloseButtonClick();
  };

  loadingCalendarStarted = () => {
    this.isCalendarLoading = true;
  };

  loadingCalendarFinished = () => {
    this.isCalendarLoading = false;
  };

  setPrice = (price: Price) => {
    this.price = price;
  };

  setPriceOptions = (priceOptions: PriceOptions) => {
    this.priceOptions = priceOptions;
  };

  handleAddToCart = () => {
    this.loadingStarted();
    const pickUpTime = this.withTime && !this.timePicker ? this.bookingDetails.fromTime + ':00' : '12:00:00';
    const dropOffTime = this.withTime && this.bookingDetails.toTime && !this.timePicker ? this.bookingDetails.toTime + ':00' : '12:00:00';
    let variationsList = [];
    if (this.selectedVariationId) {
      variationsList = Array(this.amountToBook).fill(this.selectedVariationId);
    }

    this.addItemToCart(
      {
        itemId: this.article.id,
        fullDay: !this.withDateRange && !this.withTime,
        dateRange: {
          from: dateToString(this.bookingDetails.fromDate) + 'T' + pickUpTime,
          to: dateToString(this.bookingDetails.toDate || this.bookingDetails.fromDate) + 'T' + dropOffTime,
        },
        timeSlot: this.bookingDetails.selectedTimeSlot,
        amountToAdd: this.amountToBook,
        variationsIds: variationsList,
        mileageOptionId: this.selectedMileageId,
        people: this.bookingDetails.peopleNumber,
        price: this.price,
        name: this.itemDetails.name,
      },
      () => {
        this.hideError();
        this.inCartOpened = true;
        this.loadingFinished();
      },
      () => {
        this.loadingFinished();
        this.showErrorForTime(10000);
      },
    );
  };

  hideError = () => {
    clearInterval(this.errorTimer);
    this.error = false;
  };

  showErrorForTime = (time: number = 10000) => {
    this.error = true;

    clearInterval(this.errorTimer);
    this.errorTimer = setTimeout(() => {
      this.error = false;
    }, time);
  };

  fetchPickUpDays = () => {
    this.loadingCalendarStarted();

    const selectedMonth = twoMonthDateRange(this.selectedMonth);

    fetchDaysAvailability(this.itemId, this.selectedVariationId, selectedMonth, 'pickUp', null, this.loadPickUpDays, this.loadingCalendarFinished);
  };

  loadPickUpDays = data => {
    this.daysAvailability = data.days;
    this.firstAvailableDate = data.nearestAvailableDate ? new Date(data.nearestAvailableDate) : null;
    this.loadingCalendarFinished();
  };

  setLoading = (state: boolean) => {
    this.isWaitingForApiUpdate = state;
  };

  loadingStarted = () => {
    this.isWaitingForApiUpdate = true;
  };

  loadingFinished = () => {
    this.isWaitingForApiUpdate = false;
  };

  handleChangeAmountToBook = newAmount => {
    this.amountToBook = newAmount;
    validateAndFetchPrice(this.makeValidateAndFetchPriceProps({ runWithLoading: false }));
  };

  handleVariationChange = (id: string) => {
    this.changeBookingVariation({ [this.itemId]: id });
  };

  handleMileageChange = (id: string) => {
    this.changeBookingMileage({ [this.itemId]: id });
  };

  handleMileageCartChange = (optionId: string) => {
    if (optionId !== this.selectedMileageId) {
      this.loadingStarted();

      this.changeMileageOption(
        this.itemId,
        optionId,
        () =>
          this.initForOrder(() => {
            this.loadingFinished();
          }),
        this.loadingFinished,
      );
    }
  };

  get articleAvailable(): boolean {
    return (
      !!thisItemInCart(this.itemId, this.bookingDetails.cart) || //
      (this.price?.available && available(this.picker))
    );
  }

  get isDateReady(): boolean {
    if (this.widget?.timePicker) {
      return !!this.bookingDetails.fromDate && !!this.bookingDetails.selectedTimeSlot;
    } else {
      if (!this.widget?.timePickerEnabled) {
        return !!this.bookingDetails.toDate || !!this.suspectedDropOffDate;
      } else {
        return !!this.bookingDetails.toTime;
      }
    }
  }

  get buttonText() {
    const pickDate = t('articleBooking.buttonPickDateText');
    const returnDate = t('articleBooking.buttonReturnText');
    const pickTime = t('articleBooking.buttonPickTimeText');
    const returnTime = t('articleBooking.buttonReturnTimeText');
    const returnDatePickTime = t('articleBooking.buttonPickTimeOrReturnDateText');
    const returnDateReturnTime = t('articleBooking.buttonReturnDateOrReturnTimeText');
    const selectTimeSlotHint = t('articleBooking.selectTimeSlotHint');

    if (this.price?.available) {
      const price = priceWithMileagePerTimeT(this.price, this.priceOptions, this.amountToBook, this.price.mileageOptions, this.selectedMileageId);

      return t('articleBooking.addToCart', { amount: price });
    } else {
      if (this.timePicker) {
        if (!this.bookingDetails.fromDate) {
          return pickDate;
        } else {
          return selectTimeSlotHint;
        }
      }

      if (noDateNoTime(this.picker)) {
        return pickDate;
      } else if (noDateTime(this.picker)) {
        if (!this.bookingDetails.fromDate) {
          return pickDate;
        } else if (!this.bookingDetails.fromTime) {
          return pickTime;
        } else {
          return returnTime;
        }
      } else if (dateNoTime(this.picker)) {
        if (!this.bookingDetails.fromDate) {
          return pickDate;
        } else {
          return returnDate;
        }
      } else if (dateTime(this.picker)) {
        if (!this.bookingDetails.fromDate) {
          return pickDate;
        } else {
          if (!this.bookingDetails.toDate) {
            if (!this.bookingDetails.fromTime) {
              return returnDatePickTime;
            } else {
              if (!this.suspectedDropOffDate) {
                return returnDate;
              } else {
                return returnDateReturnTime;
              }
            }
          } else {
            if (!this.bookingDetails.fromTime) {
              return pickTime;
            } else {
              return returnTime;
            }
          }
        }
      }
    }
  }

  get article(): Article {
    const itemInCart: CartItem = thisItemInCart(this.itemId, this.bookingDetails.cart);
    const cartItemMileage = this.bookingDetails.cart?.items.find(item => item.itemId === this.itemId && item.isMileageOption);
    const selectedVariation = this.itemDetails.variation?.items?.find(item => item.id === this.selectedVariationId);

    if (itemInCart) {
      if (!itemInCart.individualDiscountedPrice.priceCalculation) {
        itemInCart.individualDiscountedPrice.priceCalculation = itemInCart.priceCalculationType;
      }
      if (!itemInCart.discountedPrice.priceCalculation) {
        itemInCart.discountedPrice.priceCalculation = PriceCalculation.Interval;
      }
      if (cartItemMileage) {
        if (!cartItemMileage.mileageOption.price.priceCalculation) {
          cartItemMileage.mileageOption.price.priceCalculation = PriceCalculation.Interval;
        }
        if (!cartItemMileage.mileageOption.relativePrice.price.priceCalculation) {
          cartItemMileage.mileageOption.relativePrice.price.priceCalculation = cartItemMileage.mileageOption.relativePrice.calculationType;
        }

        itemInCart.mileageOption = cartItemMileage.mileageOption;
      }
    }

    let returnedArticle: Article = {
      id: this.itemId,
      isSelectedVariationInCart: null,
      itemInCart: itemInCart,
      mileage: this.itemDetails.mileageOptions,
      variation: this.itemDetails.variation,
      selectedVariationId: this.selectedVariationId,
      selectedMileageId: this.selectedMileageId,
      location: this.itemDetails.location,
      name: t(itemKey(this.itemDetails.id), {}, this.itemDetails.name),
      title: (this.itemDetails.title.length && t(itemTitleKey(this.itemDetails.id))) || '',
      features: (this.itemDetails.description.length && t(itemDescriptionKey(this.itemDetails.id))) || '',
      image: this.itemDetails.image,
      images: this.itemDetails.images,
      amount: this.amountToBook,
      availableAmount: this.itemDetails.quantity - this.amountToBook,
      minAmount: this.itemDetails.minQuantity,
      onChangeAmountToBook: this.handleChangeAmountToBook,
      price: { gross: this.itemDetails.grossPrice, net: this.itemDetails.netPrice },
      removable: true,
      onVariationChange: this.handleVariationChange,
      onMileageChange: thisItemInCart(this.itemId, this.bookingDetails.cart) ? this.handleMileageCartChange : this.handleMileageChange,
      onChangeAmount: this.handleChangeItemAmount,
      onDeleteArticle: null,
      priceCalculation: this.itemDetails.priceCalculationType,
      isWaiting: this.isWaitingForApiUpdate,
      available: this.articleAvailable,
      includedArticles: null,
    };

    if (!thisItemInCart(this.itemId, this.bookingDetails.cart)) {
      if (this.itemDetails.variation) {
        returnedArticle = {
          ...returnedArticle,
          variation: this.itemDetails.variation,
        };
      }

      if (this.price) {
        returnedArticle = {
          ...returnedArticle,
          price: {
            gross: this.price.gross,
            net: this.price.net,
            priceCalculation: this.price.priceCalculation,
          },
        };

        if (this.price.quantity) {
          returnedArticle = {
            ...returnedArticle,
            minAmount: this.price.quantity.min,
            availableAmount: this.price.quantity.available - this.amountToBook,
          };
        }
      }
    }

    if (itemInCart) {
      const includedArticles: IncludedArticle[] = [];
      // add included articles in case of variation or bundle (more than 1 product)
      if (itemInCart.products?.length > 0) {
        if (itemInCart.products[0].variation) {
          itemInCart.products[0].variation.items.forEach(item => {
            includedArticles.push({
              id: item.id,
              title: item.name,
              amount: item.quantity,
            });
          });
        } else if (itemInCart.products.length > 1) {
          // bundle case
          itemInCart.products.forEach(item => {
            includedArticles.push({
              id: item.id,
              title: item.name,
              amount: item.quantity,
            });
          });
        }
      }

      if (includedArticles.length) {
        returnedArticle = {
          ...returnedArticle,
          includedArticles: includedArticles,
        };
      }

      if (!this.itemDetails.variation) {
        if (this.price) {
          returnedArticle = {
            ...returnedArticle,
            amount: itemInCart.qty,
            minAmount: itemInCart.minQty,
            availableAmount: itemInCart.availableQty,
            available: true,
            price: itemInCart.individualDiscountedPrice,
            removable: itemInCart.removable,
            title: includedArticles.length === 0 ? t(itemTitleKey(itemInCart.itemId)) : '',
            onDeleteArticle: () => this.handleChangeItemAmount(0, 0),
          };
        }
      } else {
        const selectedItemVariationInCart = includedArticles.find(article => article.id === this.selectedVariationId);
        if (selectedItemVariationInCart) {
          returnedArticle = {
            ...returnedArticle,
            isSelectedVariationInCart: true,
            amount: selectedItemVariationInCart.amount,
            availableAmount: selectedVariation.quantity,
            onDeleteArticle: () => this.handleChangeItemAmount(0, selectedItemVariationInCart.amount),
          };
        } else {
          if (this.price?.quantity) {
            returnedArticle = {
              ...returnedArticle,
              minAmount: this.price.quantity.min,
              availableAmount: this.price.quantity.available - this.amountToBook,
            };
          }
        }
      }

      //make connected articles
      const cartItems: CartItem[] = this.bookingDetails.cart.items;
      let connectedArticles: ConnectedArticle[] = [];
      cartItems.forEach((cartItem: CartItem) => {
        cartItem.parentIds.forEach(parentId => {
          if (parentId === this.itemDetails.orderItemId) {
            const indexOfDuplicateArticle = connectedArticles.findIndex(connectedArticle => connectedArticle.id === parentId);
            if (indexOfDuplicateArticle === -1) {
              connectedArticles.push({
                ...cartItem,
                connectedAmount: 1,
              });
            } else {
              const duplicateArticle = connectedArticles[indexOfDuplicateArticle];
              duplicateArticle.connectedAmount += 1;
              connectedArticles[indexOfDuplicateArticle] = duplicateArticle;
            }
          }
        });
      });

      // multiply of article number
      connectedArticles = connectedArticles.map(connectedArticle => {
        return {
          ...connectedArticle,
          connectedAmount: connectedArticle.connectedAmount * returnedArticle.amount,
        };
      });

      returnedArticle.connectedArticles = connectedArticles;
    }

    if (this.amountToBook > returnedArticle.amount + returnedArticle.availableAmount) {
      this.amountToBook = returnedArticle.amount + returnedArticle.availableAmount;
    }

    return returnedArticle;
  }

  handleChangeItemAmount = (newAmount, oldAmount) => {
    if (newAmount >= 0 && newAmount < 1000) {
      if (newAmount > 0) {
        if (newAmount < this.itemDetails.minQuantity) {
          newAmount = this.itemDetails.minQuantity;
        }
      }

      const fetchCallback = () => this.fetchItemsForStep(this.loadingFinished);

      const difference = newAmount - oldAmount;
      this.loadingStarted();

      let variationsList = [];
      if (this.selectedVariationId) {
        variationsList = Array(Math.abs(difference)).fill(this.selectedVariationId);
      }

      if (difference >= 1) {
        this.addItemToCart(
          {
            itemId: this.article.id,
            amountToAdd: difference,
            variationsIds: variationsList,
            mileageOptionId: this.selectedMileageId,
            people: this.bookingDetails.peopleNumber,
            price: this.price,
            name: this.itemDetails.name,
          },
          fetchCallback,
          fetchCallback,
        );
      } else if (difference <= 0) {
        this.removeItemFromCart(
          {
            itemId: this.article.itemInCart.id,
            amountToRemove: -difference,
            variationsIds: variationsList,
          },
          fetchCallback,
          fetchCallback,
        );
      }
    }
  };

  handleShowFullGallery = (show: boolean) => {
    this.showFullGallery = show;
  };

  handleBackButtonClick = () => {
    if (this.mode === DetailsMode.Gallery || (this.mode === DetailsMode.Checkout && !this.showFullGallery)) {
      this.onCloseButtonClick();
    }

    if (this.showFullGallery) {
      this.showFullGallery = false;
    }
  };

  handleChangeMonth = (date: Date) => {
    if (!isSameMonth(this.selectedMonth, date)) {
      this.selectedMonth = date;
      const isPickUpMode: boolean = !this.withDateRange || !this.bookingDetails.fromDate;

      if (isPickUpMode) {
        this.fetchPickUpDays();
      } else {
        this.fetchDropOffDays();
      }
    }
  };

  handleDayClick = (date: Date) => {
    this.selectedMonth = date;

    if (noDateNoTime(this.picker)) {
      this.handleNoDateNoTimeClick(date);
    } else if (dateNoTime(this.picker)) {
      this.handleDateNoTimeClick(date);
    } else if (noDateTime(this.picker)) {
      this.handleNoDateTimeClick(date);
    } else if (dateTime(this.picker)) {
      this.handleDateTimeClick(date);
    }
  };

  get withTime(): boolean {
    return this.widget.timePickerEnabled;
  }

  get withDateRange(): boolean {
    return this.widget.kind !== 'date';
  }

  handleNoDateNoTimeClick = date => {
    // only case: every click on day picks a new date
    this.changeBookingDate({ fromDate: date, toDate: date });
  };

  handleNoDateTimeClick = date => {
    // only case: every click on day picks a new date
    this.changeBookingDate({ fromDate: date, toDate: date, fromTime: '', toTime: '', selectedTimeSlot: null });
  };

  handleDateNoTimeClick = date => {
    // case 1: empty calendar
    if (!this.bookingDetails.fromDate) {
      this.changeBookingDate({ fromDate: date });

      // case 2: select a drop-off or act as drop-off === pick-up
    } else if (this.bookingDetails.fromDate && !this.bookingDetails.toDate) {
      this.changeBookingDate({ toDate: date });
      this.suspectedDropOffDate = null;
    }
  };

  setPossibleHours = hours => {
    this.possibleHours = hours;
  };

  handleDateTimeClick = date => {
    // case 1: empty calendar
    if (!this.bookingDetails.fromDate) {
      this.changeBookingDate({ fromDate: date });

      // case 2: select a drop-off or act as drop-off === pick-up
    } else if (!!this.bookingDetails.fromDate && !this.bookingDetails.toDate) {
      this.changeBookingDate({ toDate: date });
      this.suspectedDropOffDate = null;
    }
  };

  setLoadingDropOff = (state: boolean) => {
    this.loadingDropOffHours = state;
  };

  handleDropOffTimeChange = value => {
    this.changeBookingDate({ toTime: value });
  };

  fetchDropOffDays = () => {
    this.loadingCalendarStarted();
    const selectedMonth = twoMonthDateRange(this.selectedMonth);

    fetchDaysAvailability(
      this.article.id,
      this.selectedVariationId,
      selectedMonth,
      'dropOff',
      this.bookingDetails.fromDate ? dateToString(this.bookingDetails.fromDate) : null,
      this.loadDropOffDays,
      this.loadingCalendarFinished,
    );
  };

  loadDropOffDays = data => {
    this.daysAvailability = data.days;
    this.firstAvailableDate = data.nearestAvailableDate ? new Date(data.nearestAvailableDate) : null;

    const pickUpString = dateToString(this.bookingDetails.fromDate);
    const pickUpAvailableAsDropOff = this.daysAvailability[pickUpString]?.state === DayState.Selectable;

    if (pickUpAvailableAsDropOff) {
      this.suspectedDropOffDate = this.bookingDetails.fromDate;
    } else {
      this.suspectedDropOffDate = null;
    }

    this.loadingCalendarFinished();
  };

  setLoadingPickup = (state: boolean) => {
    this.loadingPickUpHours = state;
  };

  get pickUpHours() {
    return Object.keys(this.possibleHours);
  }

  get dropOffHours() {
    return this.possibleHours[this.bookingDetails.fromTime] || [];
  }

  handlePickUpTimeChange = value => {
    this.changeBookingDate({ fromTime: value, toTime: '' });

    const dropOffDate = this.bookingDetails.toDate ? dateToString(this.bookingDetails.toDate) : !!this.suspectedDropOffDate ? dateToString(this.suspectedDropOffDate) : null;

    fetchPossibleHours(
      this.article.id,
      this.selectedVariationId,
      dateToString(this.bookingDetails.fromDate),
      dropOffDate,
      (hours: any) => {
        this.possibleHours = hours;
        this.loadingDropOffHours = false;

        const dropOffTimeObjectLength: number = this.possibleHours[this.bookingDetails.fromTime].length;
        if (dropOffTimeObjectLength === 1) {
          this.handleDropOffTimeChange(this.possibleHours[this.bookingDetails.fromTime][0]);
        }
      },
      () => {
        this.loadingDropOffHours = false;
      },
    );
  };

  handleEmptyCalendarClick = (day: Date) => {
    if (this.withDateRange) {
      this.changeBookingDate({ fromDate: day, fromTime: '', toDate: null, toTime: '', selectedTimeSlot: null });
    } else {
      this.changeBookingDate({ fromDate: day, fromTime: '', toDate: day, toTime: '', selectedTimeSlot: null });
    }
  };

  handleResetCalendar = (day: Date) => {
    if (this.withDateRange && isSameDay(day, this.bookingDetails.toDate)) {
      this.changeBookingDate({ toDate: null, toTime: '' });
    } else {
      if (day) {
        this.selectedMonth = day;
      }
      this.changeBookingDate({ fromDate: null, fromTime: '', toDate: null, toTime: '' });
    }
  };

  handleCheckoutButtonClick = () => {
    this.openCheckout();

    const intervalId = setInterval(() => {
      const checkoutLoaded = !!this.loadedComponents[RentwareCheckoutTemplate.name];

      if (!checkoutLoaded) {
        return;
      }

      this.onCloseButtonClick();
      clearInterval(intervalId);
    }, 100);
  };

  get dropOffPickerAvailable() {
    return (
      (this.bookingDetails.fromTime && !this.withDateRange) || ((this.bookingDetails.toDate || !!this.suspectedDropOffDate) && this.bookingDetails.fromTime && this.withDateRange)
    );
  }

  get selectedText() {
    return t('article.articleSelected');
  }

  get calculateHeaderImage() {
    return this.inCartOpened || (this.isGalleryContainerHidden && this.isInfoContainerVisible);
  }

  get picker(): Picker {
    return {
      isDateReady: this.isDateReady,
      locale: this.locale,
      onRangeDayClick: this.handleDayClick,
      onRangeMonthClick: this.handleChangeMonth,
      onResetCalendar: this.handleResetCalendar,
      withTime: this.withTime,
      withDateRange: this.withDateRange,
      isCalendarLoading: this.isCalendarLoading || this.isWaitingForApiUpdate,
      daysAvailability: this.daysAvailability,
      firstAvailableDate: this.firstAvailableDate,
      pickUpDate: this.bookingDetails.fromDate,
      pickUpTime: this.bookingDetails.fromTime,
      pickUpHours: this.pickUpHours,
      onPickUpTimeChange: this.handlePickUpTimeChange,
      loadingPickUpHours: this.loadingPickUpHours,
      dropOffPickerAvailable: this.dropOffPickerAvailable,
      dropOffDate: this.bookingDetails.toDate,
      dropOffTime: this.bookingDetails.toTime,
      dropOffHours: this.dropOffHours,
      onDropOffTimeChange: this.handleDropOffTimeChange,
      suspectedDropOffDate: this.suspectedDropOffDate,
      loadingDropOffHours: this.loadingDropOffHours,
      timeSlotPicker: this.makeTimeSlot,
      onTimeSlotChange: this.handleTimePickerOptionChange,
      onEmptyCalendarClick: this.handleEmptyCalendarClick,
      isOrder: !!this.bookingDetails.orderDetails,
    };
  }

  get detailsComponent(): DetailsComponents {
    return {
      buttonText: this.buttonText,
      selectedText: this.selectedText,
      showFullGallery: this.showFullGallery || this.mode === DetailsMode.Gallery,
      showHeaderImage: this.calculateHeaderImage,
      onGalleryContainerVisibility: this.handleGalleryContainerVisibility,
      onInfoContainerVisibility: this.handleInfoContainerVisibility,
      onCloseButtonClick: this.handleCloseButtonClick,
      onBackButtonClick: this.handleBackButtonClick,
      onShowFullGallery: this.handleShowFullGallery,
      onCheckoutButtonClick: this.handleCheckoutButtonClick,
      onAddToCart: this.handleAddToCart,
      inCartOpened: this.inCartOpened,
      cartStatus: this.cartStatus,
    };
  }

  render() {
    return (
      <rtr-article-details-template
        article={this.article}
        picker={this.picker}
        detailsComponent={this.detailsComponent}
        scrollToCalendar={this.scrollToCalendar}
        showLocation={this.showLocation}
        mode={this.mode}
        cart={this.bookingDetails.cart}
      />
    );
  }
}
